<template>
  <div>
    <div class='flex flex-wrap flex-col sm:flex-row justify-between sm:items-center gap-4'>
      <div v-if='slots.title || slots.description'>
        <h2 class='text-jananavy-900 font-medium'><slot name='title' /></h2>
        <p class='font-light text-sm'><slot name='description' /></p>
      </div>
    </div>
    <LoadingLayout :isLoading='isLoading' class='mt-8' :slownessThreshold='0'>
      <ReportNoDataWarning :warn='!view'>
        <Bar :data='view.bar' :options='barOptions' :style='view.height' />
      </ReportNoDataWarning>
    </LoadingLayout>
  </div>
</template>

<script setup>

import { computed, reactive, watch, useSlots } from 'vue';

import Chart from 'chart.js/auto';
import { Bar } from 'vue-chartjs';

import LoadingLayout from '@/components/layouts/loading-layout.vue';

import ReportNoDataWarning from '@/components/report/report-no-data-warning.vue';

import { scoreMap } from '@/kernel/scoring';
import { usePayloadRequest } from '@/kernel/api';
import { debounce } from '@/kernel/debounce';

const request = usePayloadRequest('GET', '/assessment-response/change-comparison');

const props = defineProps({
  params: Object
});

const slots = useSlots();

const barOptions = {
  plugins: {
    legend: {
      display: false
    },
    tooltip: {
      callbacks: {
        label: function (context) {
          return `${context.parsed.x.toFixed(2)}%`;
        }
      }
    }
  },
  indexAxis: 'y',
  scales: {
    x: {
      stacked: true,
      max: 100,
      ticks: {
        callback: (value, index, ticks) => {
          return `${value}%`;
        }
      }
    },
    y: {
      stacked: true,
      ticks: {
        autoSkip: false
      }
    }
  },
  maintainAspectRatio: false
}

const isLoading = computed(() => {
  return request.isLoading() || request.getData() === null;
});

const view = computed(() => {
  const data = request.getData();

  if (!data?.themes?.length || !data?.objectives?.length) {
    return null;
  }

  const targets = data.themes.toSorted((a, b) => {
    return a.header.name.localeCompare(b.header.name);
  });

  return {
    height: `height: ${(targets.length * 30) + 30}px`,
    bar: {
      labels: targets.map(target => target.header.name),
      datasets: [{
        backgroundColor: scoreMap.getColour('0', 'hex'),
        maxBarThickness: 20,
        data: targets.map(target => target.changed / target.total * 100)
      }]
    }
  }
});

const load = () => {
  if (!props.params) {
    return;
  }

  if (props.params?.year.from.toString().length !== 4) {
    return;
  }

  if (props.params?.year.to.toString().length !== 4) {
    return;
  }

  if (props.params?.year.from === props.params?.year.to) {
    return;
  }

  request.doRequest({
    params: props.params
  });
}

watch(() => props.params, debounce(() => {
  load();
}), 250);

load();

</script>
